import {produce} from 'immer';
import React, {useMemo} from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {getTitleForView, isTopLevelAnalysisView} from 'analysis-tool/data-view/AnalysisViewUtils';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {pipelineStepIsStepType, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {
  getChildViewsForView,
  getDefaultClientIdForView,
  getOutputDataForView,
  isChartView,
  isDataFrameView,
  isFileView,
} from 'pipelines/PipelineViewUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {assertNever} from 'util/Types';

export function mapChartViewsToDataFrameViews(views: Array<PipelineViewGraphQL>): Array<PipelineViewGraphQL> {
  return views.map((view) => {
    const lastNode = view.lineage[view.lineage.length - 1];
    if (lastNode?.step.stepType !== PipelineStepType.CHART_VIEW) {
      return view;
    }
    return produce(view, (draftView) => {
      const lastNode = draftView.lineage[draftView.lineage.length - 1];
      lastNode.stepOutputReference = {
        // Can safely assert that stepOutputReference and output datas exist because caller has
        // already checked that the step has output data
        ...lastNode.stepOutputReference!,
        outputStepData: lastNode.step.outputStepDatas.find((stepData) => stepData.name === 'Output dataset')!,
        rawDataIndex: 0,
      };
    });
  });
}

function matchesViewKind(view: PipelineViewGraphQL, dataKind?: PipelineStepDataKind): boolean {
  if (getOutputDataForView(view) == null) {
    return false;
  }

  switch (dataKind) {
    case undefined:
      return true;
    case PipelineStepDataKind.DATASET: {
      if (isDataFrameView(view)) {
        return true;
      }
      const lastNode = view.lineage[view.lineage.length - 1];
      return lastNode != null && pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, lastNode.step);
    }
    case PipelineStepDataKind.CHART:
      return isChartView(view);
    case PipelineStepDataKind.FILE:
      return isFileView(view);
    default:
      assertNever(dataKind);
      return false;
  }
}

function getAllDescendantViews(
  analysis: AnalysisGraphQL,
  view: PipelineViewGraphQL,
  visited: Set<string> = new Set(),
  dataKind?: PipelineStepDataKind,
  depth: number = 1
): Array<{
  view: PipelineViewGraphQL;
  depth: number;
}> {
  const viewId = getDefaultClientIdForView(view);
  if (visited.has(viewId)) {
    return [];
  }
  visited.add(viewId);

  const allDescendants = [];
  for (const childView of mapChartViewsToDataFrameViews(getChildViewsForView(analysis, view))) {
    if (!matchesViewKind(childView, dataKind) || isTopLevelAnalysisView(childView, analysis)) {
      continue;
    }
    allDescendants.push({view: childView, depth});
    allDescendants.push(...getAllDescendantViews(analysis, childView, visited, dataKind, depth + 1));
  }
  return allDescendants;
}

function getViewsForSelector(
  analysis: AnalysisGraphQL,
  topLevelOnly: boolean,
  dataKind?: PipelineStepDataKind
): Array<{
  topLevelView: PipelineViewGraphQL;
  childViews: Array<{
    view: PipelineViewGraphQL;
    depth: number;
  }>;
}> {
  const topLevelViews = analysis.views.filter(
    (view) => isTopLevelAnalysisView(view, analysis) && matchesViewKind(view, dataKind)
  );
  // Reuse visitedViewIds so that views with multiple parents only appear once
  const visitedViewIds = new Set<string>();

  return topLevelViews.map((topLevelView) => {
    const childViews = topLevelOnly
      ? []
      : getAllDescendantViews(analysis, topLevelView, visitedViewIds, dataKind);

    return {
      topLevelView,
      childViews,
    };
  });
}

function viewToDisplayName(view: PipelineViewGraphQL | null): string {
  if (view == null) {
    return 'None';
  }
  return getTitleForView(view);
}

function OptionName({view, depth}: {view: PipelineViewGraphQL | null; depth?: number}): JSX.Element {
  const style = depth != null ? {paddingLeft: depth * 10} : undefined;
  return <span style={style}>{viewToDisplayName(view)}</span>;
}

function getOptionData(
  view: PipelineViewGraphQL | null,
  depth?: number,
  disabledMessage?: string | null
): OptionData {
  return {
    name: <OptionName view={view} depth={depth} />,
    pureTextName: viewToDisplayName(view),
    value: view,
    disabled: disabledMessage != null,
    disabledMessage: disabledMessage ?? undefined,
  };
}

export interface AnalysisViewSelectorProps {
  analysis: AnalysisGraphQL;
  topLevelOnly: boolean;
  dataKind?: PipelineStepDataKind;
  selectedView: PipelineViewGraphQL | null;
  onSelect: (view: PipelineViewGraphQL | null) => void;
  getDisabledMessage?: (view: PipelineViewGraphQL) => string | null;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Selector for views in an Analysis.
 *
 * This component displays top-level views and optionally their child views.
 * Views can be filtered by their output data kind (DATASET, CHART, FILE).
 * If `DATASET` is specified, charts views will be converted to point to their output data frames and will be
 * included.
 */
export default function AnalysisViewSelector(props: AnalysisViewSelectorProps): JSX.Element {
  const viewsForSelector = useMemo(
    () => getViewsForSelector(props.analysis, props.topLevelOnly, props.dataKind),
    [props.analysis, props.topLevelOnly, props.dataKind]
  );

  const optionDatas: Array<OptionData> = [
    getOptionData(null),
    ...viewsForSelector.flatMap(({topLevelView, childViews}) => {
      const disabledMessage =
        props.getDisabledMessage != null ? props.getDisabledMessage(topLevelView) : null;
      return [
        getOptionData(topLevelView, undefined, disabledMessage),
        ...childViews.map(({view, depth}) => getOptionData(view, depth, disabledMessage)),
      ];
    }),
  ];

  return (
    <SelectDropdown
      buttonSize={ButtonSize.SMALL}
      menu={
        <SearchableMenu
          height={MenuHeight.MEDIUM}
          width={MenuWidth.FULL}
          selectedValue={props.selectedView}
          onSelect={props.onSelect}
          optionDatas={optionDatas}
        />
      }
      displayName={viewToDisplayName(props.selectedView)}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}
